import { memo, useEffect, useRef } from "react";

export const CAMERA_PAN_MS = 700;
/** Aspect caméra paysage par défaut (webcam ordi). */
const DEFAULT_CAM_ASPECT = 16 / 9;

/** Toujours miroir (selfie) — plus d’alternance invertie entre questions. */
export function mirrorForQuestionIndex(_index) {
  return true;
}

/**
 * Caméra en PAYSAGE pleine largeur (2 copies collées bord à bord).
 * Le viewport 9:16 n’est qu’un MASQUE par-dessus.
 *
 * → à la jonction des 2 panels : vrais bords cam (mur), pas le centre croppé.
 * → le pan glisse horizontalement d’un paysage entier à l’autre.
 */
function CameraPanView({
  stream,
  panning = false,
  panKey = 0,
  questionIndex = 0,
  className = "",
}) {
  const viewportRef = useRef(null);
  const videoLRef = useRef(null);
  const videoRRef = useRef(null);
  const railRef = useRef(null);
  const seatLRef = useRef(null);
  const seatRRef = useRef(null);
  const timersRef = useRef([]);
  const layoutRef = useRef({ seatW: 0, maskW: 0, centerX: 0 });

  const bindStream = (el) => {
    if (!el) return;
    if (el.srcObject !== stream) el.srcObject = stream || null;
    if (stream && el.paused) el.play().catch(() => {});
  };

  useEffect(() => {
    bindStream(videoLRef.current);
    bindStream(videoRRef.current);
  }, [stream]);

  const camAspect = () => {
    const v = videoLRef.current || videoRRef.current;
    const vw = v?.videoWidth;
    const vh = v?.videoHeight;
    if (vw > 0 && vh > 0) return vw / vh;
    return DEFAULT_CAM_ASPECT;
  };

  /** Siège = cadre paysage pleine hauteur du masque (largeur = H × aspect cam). */
  const measureLayout = () => {
    const vp = viewportRef.current;
    if (!vp) return layoutRef.current;
    const maskW = vp.clientWidth;
    const maskH = vp.clientHeight;
    const aspect = camAspect();
    const seatW = Math.max(1, Math.round(maskH * aspect));
    const centerX = (maskW - seatW) / 2;
    layoutRef.current = { seatW, maskW, maskH, centerX, aspect };
    return layoutRef.current;
  };

  const applySeatSizes = () => {
    const { seatW } = measureLayout();
    const rail = railRef.current;
    if (seatLRef.current) seatLRef.current.style.width = `${seatW}px`;
    if (seatRRef.current) seatRRef.current.style.width = `${seatW}px`;
    if (rail) rail.style.width = `${seatW * 2}px`;
    return layoutRef.current;
  };

  const setMirrors = () => {
    // Toujours mode miroir (comme un selfie)
    if (videoLRef.current) videoLRef.current.style.transform = "scaleX(-1)";
    if (videoRRef.current) videoRRef.current.style.transform = "scaleX(-1)";
  };

  const setRailPx = (px, { animate = false, durationMs = CAMERA_PAN_MS } = {}) => {
    const rail = railRef.current;
    if (!rail) return;
    if (!animate) {
      rail.style.transition = "none";
      rail.style.transform = `translate3d(${px}px, 0, 0)`;
      // eslint-disable-next-line no-unused-expressions
      rail.offsetWidth;
      rail.style.transition = "";
      return;
    }
    rail.style.transition = `transform ${durationMs}ms cubic-bezier(0.4, 0.0, 0.2, 1)`;
    rail.style.transform = `translate3d(${px}px, 0, 0)`;
  };

  const clearTimers = () => {
    timersRef.current.forEach((t) => clearTimeout(t));
    timersRef.current = [];
  };

  // Recalcule tailles quand la vidéo a ses dimensions / resize
  useEffect(() => {
    const sync = () => {
      const { centerX } = applySeatSizes();
      if (!panning) setRailPx(centerX, { animate: false });
    };

    sync();
    const v = videoLRef.current;
    v?.addEventListener("loadedmetadata", sync);
    window.addEventListener("resize", sync);

    let ro;
    if (typeof ResizeObserver !== "undefined" && viewportRef.current) {
      ro = new ResizeObserver(sync);
      ro.observe(viewportRef.current);
    }

    return () => {
      v?.removeEventListener("loadedmetadata", sync);
      window.removeEventListener("resize", sync);
      ro?.disconnect();
    };
  }, [stream, panning]);

  // État stable : masque centré sur le panneau gauche (paysage entier)
  useEffect(() => {
    if (panning) return undefined;
    clearTimers();

    setMirrors();
    const { centerX } = applySeatSizes();
    setRailPx(centerX, { animate: false });
    return undefined;
  }, [questionIndex, panning]);

  // Pan : glisse d’un paysage entier (seatW), derrière le masque 9:16
  useEffect(() => {
    clearTimers();

    if (!panning || questionIndex <= 0) {
      setMirrors();
      const { centerX } = applySeatSizes();
      setRailPx(centerX, { animate: false });
      return undefined;
    }

    setMirrors();

    const { seatW, centerX } = applySeatSizes();
    setRailPx(centerX, { animate: false });

    const startId = setTimeout(() => {
      const layout = applySeatSizes();
      // Centre du panneau droit sous le masque
      setRailPx(layout.centerX - layout.seatW, {
        animate: true,
        durationMs: CAMERA_PAN_MS,
      });
    }, 20);
    timersRef.current.push(startId);

    const endId = setTimeout(() => {
      setMirrors();
      const { centerX: cx } = applySeatSizes();
      setRailPx(cx, { animate: false });
    }, CAMERA_PAN_MS + 40);
    timersRef.current.push(endId);

    return () => clearTimers();
  }, [panning, panKey, questionIndex]);

  return (
    <div
      ref={viewportRef}
      className={`mf-cam-viewport ${panning ? "mf-cam-viewport--panning" : ""} ${className}`}
      data-testid="camera-pan-view"
    >
      <div ref={railRef} className="mf-cam-rail">
        <div ref={seatLRef} className="mf-cam-seat">
          <video ref={videoLRef} muted playsInline autoPlay className="mf-cam-video" />
        </div>
        <div ref={seatRRef} className="mf-cam-seat">
          <video ref={videoRRef} muted playsInline autoPlay className="mf-cam-video" />
        </div>
      </div>
      <div className="mf-cam-vignette" aria-hidden />
    </div>
  );
}

export default memo(CameraPanView);
